// Lazy-loads the Google Maps JavaScript API (with Places library) on demand.
//
// The API is a 1.25 MB uncompressed JS bundle. Previously it lived in a
// <script> tag in public/index.html so every visitor — including someone
// who lands on the homepage and never uses the estimator — paid the full
// parse/eval cost on first paint. Killed our mobile Lighthouse score.
//
// Now: we inject the <script> only when a component that ACTUALLY needs
// Maps (address autocomplete, timekeeping map, careers page map) first
// mounts. A shared module-level promise dedupes concurrent callers so we
// never load the script twice, even if three autocomplete inputs mount
// simultaneously.
//
// Usage:
//   const { ready, error } = useGoogleMapsScript();
//   if (ready) { /* window.google.maps.places is now available */ }

import { useEffect, useState } from "react";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SRC = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places,drawing&loading=async`;

let loaderPromise = null;

/**
 * Kick off (or reuse an in-flight) Google Maps script load. Returns a
 * Promise that resolves when window.google.maps.places is available.
 */
export function loadGoogleMapsScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.maps?.places) return Promise.resolve();
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    // Reuse an existing <script> tag if some other code already injected one.
    const existing = document.querySelector('script[data-google-maps-loader="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", (e) => reject(e));
      return;
    }
    const s = document.createElement("script");
    s.src = SRC;
    s.async = true;
    s.defer = true;
    s.setAttribute("data-google-maps-loader", "1");
    s.onload = () => resolve();
    s.onerror = (e) => {
      loaderPromise = null;  // allow retry on failure
      reject(e);
    };
    document.head.appendChild(s);
  });
  return loaderPromise;
}

/**
 * React hook: returns { ready, error } as the script loads.
 * Safe to call from many components at once — they'll share one load.
 */
export function useGoogleMapsScript() {
  const [state, setState] = useState(() => ({
    ready: !!(typeof window !== "undefined" && window.google?.maps?.places),
    error: null,
  }));

  useEffect(() => {
    if (state.ready) return;
    let mounted = true;
    loadGoogleMapsScript()
      .then(() => { if (mounted) setState({ ready: true, error: null }); })
      .catch((err) => { if (mounted) setState({ ready: false, error: err }); });
    return () => { mounted = false; };
  }, []);

  return state;
}
